import { APP_INITIALIZER, Provider, inject } from '@angular/core';
import { GtmInitializationService, ReturnUrlService } from '@lx/quote-tool/lite/shared/services';
import { ViewPageTrackingService } from '@lx/quote-tool/shared/google-analytics';
import { HostDataReaderService } from '@lx/quote-tool/shared/services';
import { APPLICATION_CONFIGURATION_SERVICE, CompanyThemeLoaderService } from '@lx/shared/app-config';
import { configFactory } from '@lx/shared/app-init';
import { DATA_LAYER_SERVICE } from '@lx/shared/google-analytics';
import { Company, RuntimeEnvironment } from '@lx/shared/types';
import { ensureStringOrUndefined } from '@lx/shared/utils';
import { OnlineQuoteToolGoogleAnalyticsDataLayerService, UpdatedQuoteTrackingService } from '../google-analytics';
import { AppInitializerService } from '../services';
import { COMPANY } from '@lx/shared/tokens';

/**
 * Gets all application initializers for Online Quote Tool.
 * These will run before the application bootstraps.
 *
 * This provider ensures that the application store, persistent state,
 * and return URL service are fully initialized before Angular renders any components.
 *
 * Note: Store initialization happens inside AppInitializerService.initialize()
 * to ensure proper initialization order (mode data → store → country/currency → persistence).
 */
export const getAppInitializers = (runtimeEnvironment: RuntimeEnvironment) => {
  return [
    ...getCompanyThemeLoaderApplicationProvider(runtimeEnvironment.COMPANY),
    ...getHostDataReaderApplicationProvider(),
    ...getAppInitializerServiceProvider(runtimeEnvironment),
    ...getDataLayerServiceProvider(),
    ...getViewPageTrackingServiceProvider(),
    ...getUpdatedQuoteTrackingServiceProvider(),
    ...getGtmInitializationProvider(runtimeEnvironment),
  ];
};

/**
 * Gets the application provider for the AppInitializerService.
 * This ensures that the store and persistent state are initialized before any components render.
 * @param runtimeEnvironment
 */
const getAppInitializerServiceProvider = (runtimeEnvironment: RuntimeEnvironment): Provider[] => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const configService = inject(APPLICATION_CONFIGURATION_SERVICE);
        const appInitializerService = inject(AppInitializerService);
        const returnUrlService = inject(ReturnUrlService);

        return async () => {
          // Load configuration from backend API
          const configuration = await configFactory(runtimeEnvironment, configService)();

          // Initialize return URL service with configuration
          // This must happen BEFORE any component renders to ensure safe navigation
          const returnUrl = ensureStringOrUndefined(configuration.ONLINE_QUOTE_TOOL_RETURN_URL);
          returnUrlService.initialize(returnUrl);

          // Await the initialization to ensure country/currency detection completes
          // before any component (including AppComponent.ngOnInit) runs
          await appInitializerService.initialize(configuration);
        };
      },
      multi: true,
    },
  ];
};

/**
 * Gets the application provider for the company configuration
 * Provides COMPANY token and an APP_INITIALIZER to load company-specific theme CSS.
 *
 * The APP_INITIALIZER performs:
 * 1. Blocks bootstrap until the company-specific theme CSS is fully loaded (network load complete)
 * 2. Company resolution and persistence to sessionStorage is handled at bootstrap time
 *    via main.ts using resolveCompany() and saveCompanyToStorage() functions
 *
 * Bootstrap is blocked via a Promise returned from companyThemeLoaderService.initialize()
 * that resolves only when the theme stylesheet is fully loaded, ensuring the theme
 * is applied before any components render.
 * @param company - The company resolved during bootstrap
 */
export const getCompanyThemeLoaderApplicationProvider = (company: Company): Provider[] => {
  return [
    { provide: COMPANY, useValue: company },
    {
      provide: APP_INITIALIZER,
      useFactory: (companyThemeLoaderService: CompanyThemeLoaderService) => () => companyThemeLoaderService.initialize(),
      deps: [CompanyThemeLoaderService],
      multi: true,
    },
  ];
};

/**
 * Provides the APP_INITIALIZER for initializing the HostDataReaderService.
 *
 * This ensures that query parameters are read from the URL before any other
 * initialization logic (like mode detection) tries to access them.
 *
 * Execution Order:
 * 1. CompanyThemeLoaderService.initialize() - Loads company-specific theme CSS and favicon
 * 2. HostDataReaderService.initializeQueryParameters() - Reads URL params
 * 3. AppInitializerService.initialize() - Uses params for mode detection
 */
export const getHostDataReaderApplicationProvider = () => {
  return [
    HostDataReaderService,
    {
      provide: APP_INITIALIZER,
      useFactory: (hostDataReaderService: HostDataReaderService) => () => hostDataReaderService.initializeQueryParameters(),
      deps: [HostDataReaderService],
      multi: true,
    },
  ];
};

/**
 * Gets the application provider for the data layer service.
 * This registers the Google Analytics data layer service for dependency injection.
 *
 * @returns Array of providers for data layer service
 */
const getDataLayerServiceProvider = (): Provider[] => {
  return [{ provide: DATA_LAYER_SERVICE, useExisting: OnlineQuoteToolGoogleAnalyticsDataLayerService }];
};

/**
 * Gets the application provider for the view page tracking service.
 * It will start tracking the view page events when the application is initialized.
 *
 * @returns Array of providers for view page tracking initialization
 */
const getViewPageTrackingServiceProvider = (): Provider[] => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: (viewPageTrackingService: ViewPageTrackingService) => () => viewPageTrackingService.startTrackingViewPageEvents(),
      deps: [ViewPageTrackingService],
      multi: true,
    },
  ];
};

/**
 * Gets the application provider for the updated quote tracking service.
 * It will start tracking quote update events when the application is initialized.
 *
 * @returns Array of providers for updated quote tracking initialization
 */
const getUpdatedQuoteTrackingServiceProvider = (): Provider[] => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: (updatedQuoteTrackingService: UpdatedQuoteTrackingService) => () => updatedQuoteTrackingService.startTrackingUpdatedQuoteEvent(),
      deps: [UpdatedQuoteTrackingService, DATA_LAYER_SERVICE],
      multi: true,
    },
  ];
};

/**
 * Gets the application provider for GTM initialization.
 * The GTM service must be initialized immediately after configuration is loaded to ensure
 * the container ID is available, and the GTM script is injected before page views are tracked.
 * @param runtimeEnvironment The runtime environment (e.g., 'production', 'development')
 * @returns Array of providers for GTM initialization
 */
const getGtmInitializationProvider = (runtimeEnvironment: RuntimeEnvironment): Provider[] => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const configService = inject(APPLICATION_CONFIGURATION_SERVICE);
        const gtmService = inject(GtmInitializationService);

        return async () => {
          // GTM service must be initialized immediately after configuration is loaded to ensure
          // the container ID is available, the GTM script is injected before page views are tracked
          const configData = await configFactory(runtimeEnvironment, configService)();

          const gtmContainerId = ensureStringOrUndefined(configData.ONLINE_QUOTE_TOOL_GTM_CONTAINER_ID);
          gtmService.initialize(gtmContainerId);
        };
      },
      multi: true,
    },
  ];
};
